import React from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Home,
  Info,
  LoaderCircle,
  MapPin,
  Upload,
  Wrench,
  X,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getDossierDetail } from "../dossier/steps/homebuyerDossierApi";
import "./HomebuyerOssPropertyDetails.css";
import { EUROPEAN_COUNTRIES } from "../../../data/countries";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const EPC_ACCEPT = ".pdf";
const OSS_PROPERTY_STORAGE_KEY = "legup_hb_oss_property_details";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const DEFAULT_PROPERTY_DETAILS = {
  property_id: null,

  market_region_id: null,
  market_region: null,

  property_type: "",
  building_condition: "",
  surface_sqm: "",
  number_of_rooms: "",
  year_of_build: "",
  has_lift: "",
  comfort_type: "",

  street_name: "",
  house_number: "",
  city: "",
  postal_code: "",
  country: "",
  apartment_number: "",
  full_address: "",

  latitude: null,
  longitude: null,
  place_id: "",

  epc_rating_current: "",
  epc_rating_target: "",
  kwh_sqm_year_current: "",
  kwh_sqm_year_target: "",

  heating_type: "",
  cooling_type: "",

  selected_measures: [],
};

const MEASURE_GROUPS = [
  {
    key: "envelope",
    items: [
      "improve_insulation",
      "replace_old_windows",
      "replace_external_doors",
      "fix_humidity_condensation",
      "improve_airtightness",
      "facade_insulation",
      "roof_waterproofing_insulation",
      "floor_insulation",
      "shading_solar_protection",
    ],
  },
  {
    key: "heating_cooling",
    items: [
      "improve_heating",
      "improve_cooling",
      "replace_old_boiler",
      "upgrade_heating_controls",
      "install_underfloor_heating",
      "upgrade_radiators_emitters",
      "upgrade_ventilation_air_quality",
      "install_mechanical_ventilation",
      "install_heat_pump",
      "dhw_system_upgrade",
    ],
  },
  {
    key: "electricity_renewables",
    items: [
      "install_solar_panels",
      "install_battery_storage",
      "upgrade_electrical_panel",
      "replace_lighting_led",
      "smart_home_automation",
      "install_ev_charger",
      "install_solar_water_heating",
      "metering_monitoring_upgrades",
    ],
  },
  {
    key: "general_renovations",
    items: [
      "improve_lighting",
      "kitchen_renovation",
      "bathroom_renovation",
      "interior_refurbishment",
      "accessibility_improvements",
      "structural_repairs",
      "flooring_replacement",
      "painting_finishing_works",
      "facade_repairs",
    ],
  },
];

function loadGoogleMaps(apiKey) {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (window.__legupGoogleMapsPromise) return window.__legupGoogleMapsPromise;

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("legup-google-maps-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = "legup-google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=en`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

function getAddressPart(place, type, useShortName = false) {
  const component = place?.address_components?.find((c) =>
    c.types?.includes(type),
  );
  if (!component) return "";
  return useShortName ? component.short_name || "" : component.long_name || "";
}

function getActiveEpcDocument(dossier) {
  const epcRequirement = dossier?.document_requirements?.find(
    (req) => req?.document_type_code === "epc",
  );

  const activeFile =
    epcRequirement?.files?.find((file) => file?.is_active) ||
    epcRequirement?.files?.[0] ||
    null;

  const activeVersion =
    activeFile?.latest_version ||
    activeFile?.versions?.find((version) => version?.is_active) ||
    activeFile?.versions?.[0] ||
    null;

  return {
    epc_document_requirement_id: epcRequirement?.id || null,
    epc_document_file_id: activeFile?.id || null,
    epc_document_version_id: activeVersion?.id || null,
    epc_file_name:
      activeVersion?.original_filename ||
      activeFile?.original_filename ||
      activeFile?.display_name ||
      "",
    epc_file_url: activeVersion?.file_url || activeFile?.file_url || "",
  };
}

function mapDossierToPropertyDetails(dossier) {
  const property =
    dossier?.property_information || dossier?.linked_property || {};

  const epcDocument = getActiveEpcDocument(dossier);

  const marketRegion =
    property?.market_region || dossier?.market_region || null;

  return {
    ...DEFAULT_PROPERTY_DETAILS,
    property_id: property?.id || property?.property_id || null,

    property_type: property?.property_type || "",
    building_condition: property?.building_condition || "",
    surface_sqm: property?.surface_sqm || "",
    number_of_rooms: property?.number_of_rooms || "",
    year_of_build: property?.year_of_build || "",

    has_lift:
      property?.has_lift === true || property?.has_lift === "YES"
        ? "YES"
        : property?.has_lift === false || property?.has_lift === "NO"
          ? "NO"
          : "",

    comfort_type: property?.comfort_type || "",
    street_name: property?.street_name || "",
    house_number: property?.house_number || "",
    city: property?.city || "",
    postal_code: property?.postal_code || "",
    country: property?.country || dossier?.country_of_property_purchase || "",
    apartment_number: property?.apartment_number || "",
    full_address: property?.full_address || "",
    latitude: property?.latitude || null,
    longitude: property?.longitude || null,
    place_id: property?.place_id || "",
    market_region_id:
      property?.market_region_id ||
      marketRegion?.id ||
      dossier?.market_region_id ||
      null,

    market_region: marketRegion,
    epc_rating_current: property?.epc_rating_current || "",
    epc_rating_target: property?.epc_rating_target || "",
    kwh_sqm_year_current: property?.kwh_sqm_year_current || "",
    kwh_sqm_year_target: property?.kwh_sqm_year_target || "",
    heating_type: property?.heating_type || "",
    cooling_type: property?.cooling_type || "",

    ...epcDocument,
  };
}

function normalizeMeasures(measures = []) {
  if (!Array.isArray(measures)) return [];

  return measures
    .map((item) => {
      if (typeof item === "string") return item;
      return (
        item?.code ||
        item?.key ||
        item?.measure_code ||
        item?.measure_type ||
        ""
      );
    })
    .filter(Boolean);
}

function formatNumber(value, suffix = "", t) {
  if (value === null || value === undefined || value === "") {
    return t("common.notAvailable", "—");
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);

  return `${parsed.toLocaleString("en-US")}${suffix}`;
}

function getSavedState(
  locationState = {},
  storageKey = OSS_PROPERTY_STORAGE_KEY,
) {
  try {
    const saved = sessionStorage.getItem(storageKey);
    const parsed = saved ? JSON.parse(saved) : {};

    return {
      propertyDetails:
        locationState.propertyDetails ||
        parsed.propertyDetails ||
        DEFAULT_PROPERTY_DETAILS,
      selectedMeasures:
        locationState.selectedMeasures ||
        locationState.existingMeasures ||
        parsed.selectedMeasures ||
        [],
      notes: locationState.notes || parsed.notes || "",
      epcFileName: locationState.epcFileName || parsed.epcFileName || "",
    };
  } catch {
    return {
      propertyDetails: DEFAULT_PROPERTY_DETAILS,
      selectedMeasures: [],
      notes: "",
      epcFileName: "",
    };
  }
}

function ErrorToast({ errors = [], closing = false, onClose, t }) {
  if (!errors.length) return null;

  return (
    <div className={`hb-oss-error-toast ${closing ? "is-closing" : ""}`}>
      <div className="hb-oss-error-toast__header">
        <div>
          <AlertCircle size={18} />
          <strong>{t("errors.modalTitle", "Before continuing")}</strong>
        </div>

        <button type="button" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <ul>
        {errors.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function RequiredLabel({ children }) {
  return (
    <>
      {children}
      <span className="hb-oss-required">*</span>
    </>
  );
}

export default function HomebuyerOssPropertyDetails() {
  const { t } = useTranslation("homebuyer-oss-property-details");
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { dashboardSummary } = useOutletContext();

  const dossierId =
    location.state?.dossierId || dashboardSummary?.dossier_id || null;

  const storageKey = React.useMemo(() => {
    return `${OSS_PROPERTY_STORAGE_KEY}_${dossierId || "no_dossier"}`;
  }, [dossierId]);

  const restored = React.useMemo(
    () => getSavedState(location.state, storageKey),
    [location.state, storageKey],
  );

  React.useEffect(() => {
    if (!dossierId && !location.state?.propertyDetails) {
      setPropertyDetails(DEFAULT_PROPERTY_DETAILS);
      setSelectedMeasures([]);
      setNotes("");
      setEpcFile(null);
      setEpcFileName("");
    }
  }, [dossierId, location.state?.propertyDetails]);

  const dossierStatus = dashboardSummary?.dossier_status;
  const isRejectedDossier = dossierStatus === "REJECTED";
  const isChangeOssMode = location.state?.changeOssMode === true;

  const shouldPrefill =
    Boolean(dossierId) &&
    !isRejectedDossier &&
    (Boolean(location.state?.prefillFromDossier) ||
      Boolean(dashboardSummary?.has_dossier) ||
      Boolean(dashboardSummary?.has_submitted_dossier) ||
      isChangeOssMode);

  const [loading, setLoading] = React.useState(shouldPrefill);
  const [toastErrors, setToastErrors] = React.useState([]);
  const [mapsError, setMapsError] = React.useState("");
  const [propertyDetails, setPropertyDetails] = React.useState(
    restored.propertyDetails,
  );
  const [epcFile, setEpcFile] = React.useState(location.state?.epcFile || null);
  const [epcFileName, setEpcFileName] = React.useState(
    restored.epcFileName || restored.propertyDetails?.epc_file_name || "",
  );
  const [selectedMeasures, setSelectedMeasures] = React.useState(
    restored.selectedMeasures,
  );
  const [notes, setNotes] = React.useState(restored.notes);
  const [submitting, setSubmitting] = React.useState(false);
  const [isToastClosing, setIsToastClosing] = React.useState(false);
  const addressInputRef = React.useRef(null);

  const [marketRegions, setMarketRegions] = React.useState([]);

  const [loadingMarketRegions, setLoadingMarketRegions] = React.useState(false);

  React.useEffect(() => {
    const countryCode = String(propertyDetails.country || "")
      .trim()
      .toUpperCase();

    if (!countryCode) {
      setMarketRegions([]);

      return;
    }

    let cancelled = false;

    async function loadMarketRegions() {
      try {
        setLoadingMarketRegions(true);

        const params = new URLSearchParams({
          country_code: countryCode,

          /*
           * This route eventually connects
           * to a lender too, so only expose
           * operational markets.
           */
          require_lender: "true",
        });

        const response = await fetch(
          `${API_BASE}/api/market-regions/?${params.toString()}`,
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.detail || "Could not load market regions.");
        }

        if (cancelled) {
          return;
        }

        const results = Array.isArray(data?.results) ? data.results : [];

        setMarketRegions(results);

        if (results.length === 1) {
          const region = results[0];

          setPropertyDetails((previous) => ({
            ...previous,

            market_region_id: region.id,

            market_region: region,
          }));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Could not load market regions:", error);

          setMarketRegions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingMarketRegions(false);
        }
      }
    }

    loadMarketRegions();

    return () => {
      cancelled = true;
    };
  }, [propertyDetails.country]);

  React.useEffect(() => {
    const dashboardMeasures = normalizeMeasures(
      dashboardSummary?.selected_measures,
    );

    if (dashboardMeasures.length > 0 && selectedMeasures.length === 0) {
      setSelectedMeasures(dashboardMeasures);
    }

    if (!notes && dashboardSummary?.oss_request_notes) {
      setNotes(dashboardSummary.oss_request_notes);
    }
  }, [
    dashboardSummary?.selected_measures,
    dashboardSummary?.oss_request_notes,
    selectedMeasures.length,
    notes,
  ]);

  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          propertyDetails,
          selectedMeasures,
          notes,
          epcFileName,
        }),
      );
    } catch (error) {
      console.error("Could not save OSS property step:", error);
    }
  }, [propertyDetails, selectedMeasures, notes, epcFileName, storageKey]);

  React.useEffect(() => {
    let active = true;

    async function loadDossierProperty() {
      if (!shouldPrefill || !dossierId) {
        setLoading(false);
        return;
      }

      const hasRestoredProperty =
        restored?.propertyDetails?.full_address ||
        restored?.propertyDetails?.property_id;

      if (
        !isChangeOssMode &&
        !location.state?.prefillFromDossier &&
        (location.state?.propertyDetails || hasRestoredProperty)
      ) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const accessToken = await getAccessTokenSilently();
        const dossier = await getDossierDetail({
          accessToken,
          dossierId,
        });
        console.log(dossier);
        if (!active) return;

        const mappedProperty = mapDossierToPropertyDetails(dossier);

        setPropertyDetails(mappedProperty);

        if (mappedProperty.epc_file_name) {
          setEpcFileName(mappedProperty.epc_file_name);
        }
      } catch (err) {
        if (!active) return;
        setToastErrors([err?.message || t("errors.loadFailed")]);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadDossierProperty();

    return () => {
      active = false;
    };
  }, [
    dossierId,
    getAccessTokenSilently,
    shouldPrefill,
    t,
    restored?.propertyDetails?.full_address,
    restored?.propertyDetails?.property_id,
    location.state?.propertyDetails,
    location.state?.prefillFromDossier,
    isChangeOssMode,
  ]);

  React.useEffect(() => {
    let mounted = true;

    async function initAutocomplete() {
      if (!GOOGLE_MAPS_API_KEY || !addressInputRef.current) return;

      try {
        const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);
        if (!mounted || !google || !addressInputRef.current) return;

        const autocomplete = new google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            fields: [
              "formatted_address",
              "geometry",
              "place_id",
              "address_components",
            ],
            types: ["address"],
          },
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (!place?.geometry?.location) {
            return;
          }

          const countryCode = getAddressPart(place, "country", true)
            .trim()
            .toUpperCase();

          const city =
            getAddressPart(place, "locality") ||
            getAddressPart(place, "postal_town") ||
            getAddressPart(place, "administrative_area_level_2");

          setPropertyDetails((previous) => {
            const countryChanged =
              countryCode && countryCode !== previous.country;

            return {
              ...previous,
              street_name:
                getAddressPart(place, "route") ||
                getAddressPart(place, "street_address"),
              house_number: getAddressPart(place, "street_number"),
              city,
              postal_code: getAddressPart(place, "postal_code"),
              country: countryCode,
              full_address: place.formatted_address || "",
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              place_id: place.place_id || "",
              ...(countryChanged
                ? {
                    market_region_id: null,

                    market_region: null,
                  }
                : {}),
            };
          });
        });
      } catch {
        setMapsError(t("errors.mapsLoadFailed"));
      }
    }

    initAutocomplete();

    return () => {
      mounted = false;
    };
  }, [t]);

  React.useEffect(() => {
    if (!toastErrors.length) return undefined;

    setIsToastClosing(false);

    const closeTimer = window.setTimeout(() => {
      setIsToastClosing(true);
    }, 5200);

    const removeTimer = window.setTimeout(() => {
      setToastErrors([]);
      setIsToastClosing(false);
    }, 5600);

    return () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(removeTimer);
    };
  }, [toastErrors]);

  function toggleMeasure(measure) {
    setSelectedMeasures((prev) =>
      prev.includes(measure)
        ? prev.filter((item) => item !== measure)
        : [...prev, measure],
    );
  }

  function handleEpcUpload(event) {
    const file = event.target.files?.[0] || null;
    setEpcFile(file);
    setEpcFileName(file?.name || "");
    setToastErrors([]);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    let nextValue = value;

    if (
      [
        "surface_sqm",
        "number_of_rooms",
        "year_of_build",
        "kwh_sqm_year_current",
        "kwh_sqm_year_target",
      ].includes(name)
    ) {
      nextValue = value.replace(/[^\d.]/g, "");
    }

    setPropertyDetails((previous) => {
      const next = {
        ...previous,
        [name]: nextValue,
      };

      if (name === "country" && nextValue !== previous.country) {
        next.market_region_id = null;

        next.market_region = null;
      }

      if (name === "market_region_id") {
        const selectedRegion =
          marketRegions.find(
            (region) => String(region.id) === String(nextValue),
          ) || null;

        next.market_region_id = selectedRegion?.id || null;

        next.market_region = selectedRegion;
      }

      return next;
    });

    setToastErrors([]);
  }

  function renderInputWithSuffix(name, value, placeholder, suffix) {
    return (
      <div className="hb-oss-input-with-suffix">
        <input
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
        />
        <span className="hb-oss-input-with-suffix__suffix">{suffix}</span>
      </div>
    );
  }

  function validateBeforeNext() {
    const errors = [];

    if (!propertyDetails.full_address?.trim()) {
      errors.push(t("errors.addressRequired", "Property address is required."));
    }

    if (!propertyDetails.property_type) {
      errors.push(
        t("errors.propertyTypeRequired", "Property type is required."),
      );
    }

    if (!propertyDetails.building_condition) {
      errors.push(
        t(
          "errors.buildingConditionRequired",
          "Building condition is required.",
        ),
      );
    }

    if (!propertyDetails.surface_sqm) {
      errors.push(t("errors.surfaceRequired", "Floor area is required."));
    }

    if (!propertyDetails.year_of_build) {
      errors.push(t("errors.yearRequired", "Year of build is required."));
    }

    if (!propertyDetails.street_name?.trim()) {
      errors.push(t("errors.streetRequired", "Street name is required."));
    }

    if (!propertyDetails.house_number?.trim()) {
      errors.push(t("errors.houseNumberRequired", "House number is required."));
    }

    if (!propertyDetails.city) {
      errors.push(t("errors.cityRequired", "City / region is required."));
    }

    if (!propertyDetails.postal_code?.trim()) {
      errors.push(t("errors.postalCodeRequired", "Postal code is required."));
    }

    if (!propertyDetails.country) {
      errors.push(t("errors.countryRequired", "Country is required."));
    }

    if (!propertyDetails.epc_rating_current) {
      errors.push(
        t("errors.epcRatingRequired", "Current EPC rating is required."),
      );
    }

    if (!propertyDetails.kwh_sqm_year_current) {
      errors.push(
        t(
          "errors.kwhCurrentRequired",
          "Current energy consumption is required.",
        ),
      );
    }

    if (!propertyDetails.market_region_id) {
      errors.push(
        t("errors.marketRegionRequired", "Market region is required."),
      );
    }

    if (!propertyDetails.heating_type) {
      errors.push(t("errors.heatingTypeRequired", "Heating type is required."));
    }

    if (!propertyDetails.cooling_type) {
      errors.push(t("errors.coolingTypeRequired", "Cooling type is required."));
    }

    if (selectedMeasures.length === 0) {
      errors.push(
        t(
          "errors.measuresRequired",
          "Select at least one improvement measure.",
        ),
      );
    }

    return errors;
  }

  async function handleNext() {
    const errors = validateBeforeNext();

    if (errors.length) {
      setToastErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setToastErrors([]);

      navigate("/hb/oss/partners/renovation", {
        state: {
          dossierId,

          marketRegionId: propertyDetails.market_region_id,

          marketRegion: propertyDetails.market_region,

          propertyDetails,

          selectedMeasures,

          notes,

          epcFile,
          epcFileName,

          requestType: "RENOVATION",

          fromPropertyDetails: true,

          changeOssMode: location.state?.changeOssMode === true,

          previousOssRequestId: location.state?.previousOssRequestId,

          previousOssName: location.state?.previousOssName,
          previousOssOrganisationId: location.state?.previousOssOrganisationId,
        },
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="hb-oss-property-page">
        <div className="hb-oss-property-shell hb-oss-property-shell--loading">
          <LoaderCircle className="hb-oss-spin" size={28} />
          <p>{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hb-oss-property-page">
      <ErrorToast
        errors={toastErrors}
        closing={isToastClosing}
        onClose={() => {
          setIsToastClosing(true);
          window.setTimeout(() => {
            setToastErrors([]);
            setIsToastClosing(false);
          }, 350);
        }}
        t={t}
      />

      <div className="hb-oss-property-shell">
        {isChangeOssMode ? (
          <div className="hb-oss-property-change-notice" role="status" style={{
            background: "#eef5e6",
            border: "1px solid #b8c9a7",
            borderRadius: 12,
            padding: "14px 18px",
            marginBottom: 18,
            color: "#31402f",
          }}>
            <strong>{t("changeOss.title", "Change One-Stop Shop")}</strong>
            <p style={{ margin: "6px 0 0" }}>
              {t("changeOss.description", "Select another One-Stop Shop without disconnecting manually. Your existing project and financing records will be retained. Review your property details before continuing.")}
            </p>
          </div>
        ) : null}
        <div className="hb-oss-property-hero">
          <div>
            <h1 className="hb-oss-property-hero__title">{t("title")}</h1>
            <p className="hb-oss-property-hero__subtitle">{t("subtitle")}</p>
          </div>

          <div className="hb-oss-property-hero__step">{t("stepLabel")}</div>
        </div>

        <div className="hb-oss-property-card">
          <div className="hb-oss-property-card__header">
            <div>
              <h2>{t("sections.propertyDetails")}</h2>
              <p>{t("sections.propertyDetailsHint")}</p>
            </div>

            {shouldPrefill ? (
              <div className="hb-oss-prefilled-badge">
                <CheckCircle2 size={16} />
                <span>{t("prefilled")}</span>
              </div>
            ) : null}
          </div>

          <div className="hb-oss-property-layout">
            <div className="hb-oss-property-left">
              <div className="hb-oss-property-grid">
                <div className="hb-oss-field hb-oss-field--full">
                  <label>
                    <RequiredLabel>{t("fields.searchAddress")}</RequiredLabel>
                  </label>
                  <input
                    ref={addressInputRef}
                    name="full_address"
                    value={propertyDetails.full_address}
                    onChange={handleChange}
                    placeholder={t("placeholders.searchAddress")}
                  />
                  {!GOOGLE_MAPS_API_KEY ? (
                    <span className="hb-oss-field-hint">
                      {t("messages.mapsKeyMissing")}
                    </span>
                  ) : null}
                  {mapsError ? (
                    <span className="hb-oss-field-error">{mapsError}</span>
                  ) : null}
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.propertyType")}</RequiredLabel>
                  </label>
                  <select
                    name="property_type"
                    value={propertyDetails.property_type}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="APARTMENT_FLAT">
                      {t("options.propertyType.apartmentFlat")}
                    </option>
                    <option value="STUDIO_APARTMENT">
                      {t("options.propertyType.studioApartment")}
                    </option>
                    <option value="SINGLE_FAMILY_HOUSE_DETACHED">
                      {t("options.propertyType.singleFamilyHouseDetached")}
                    </option>
                    <option value="SEMI_DETACHED_HOUSE">
                      {t("options.propertyType.semiDetachedHouse")}
                    </option>
                    <option value="TERRACED_ROW_HOUSE">
                      {t("options.propertyType.terracedRowHouse")}
                    </option>
                    <option value="DUPLEX_MAISONETTE">
                      {t("options.propertyType.duplexMaisonette")}
                    </option>
                    <option value="TOWNHOUSE">
                      {t("options.propertyType.townhouse")}
                    </option>
                    <option value="MULTI_FAMILY_RESIDENTIAL_BUILDING">
                      {t("options.propertyType.multiFamilyResidentialBuilding")}
                    </option>
                    <option value="OTHER">
                      {t("options.propertyType.other")}
                    </option>
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>
                      {t("fields.buildingCondition")}
                    </RequiredLabel>
                  </label>
                  <select
                    name="building_condition"
                    value={propertyDetails.building_condition}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="EXCELLENT">
                      {t("options.buildingCondition.excellent")}
                    </option>
                    <option value="VERY_GOOD">
                      {t("options.buildingCondition.veryGood")}
                    </option>
                    <option value="GOOD">
                      {t("options.buildingCondition.good")}
                    </option>
                    <option value="FAIR_AVERAGE">
                      {t("options.buildingCondition.fairAverage")}
                    </option>
                    <option value="POOR">
                      {t("options.buildingCondition.poor")}
                    </option>
                    <option value="OTHER">
                      {t("options.buildingCondition.other")}
                    </option>
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.floorArea")}</RequiredLabel>
                  </label>
                  {renderInputWithSuffix(
                    "surface_sqm",
                    propertyDetails.surface_sqm,
                    t("placeholders.surfaceSqm"),
                    t("units.squareMeters"),
                  )}
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.numberOfRooms")}</label>
                  <input
                    name="number_of_rooms"
                    value={propertyDetails.number_of_rooms}
                    onChange={handleChange}
                    placeholder={t("placeholders.numberOfRooms")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.yearOfBuild")}</RequiredLabel>
                  </label>
                  <select
                    name="year_of_build"
                    value={propertyDetails.year_of_build}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    {Array.from(
                      { length: new Date().getFullYear() - 1799 },
                      (_, i) => {
                        const year = String(new Date().getFullYear() - i);
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      },
                    )}
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.hasLift")}</label>
                  <select
                    name="has_lift"
                    value={propertyDetails.has_lift}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="YES">{t("options.yes")}</option>
                    <option value="NO">{t("options.no")}</option>
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.comfortType")}</label>
                  <select
                    name="comfort_type"
                    value={propertyDetails.comfort_type}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="BASIC">
                      {t("options.comfortType.basic")}
                    </option>
                    <option value="STANDARD">
                      {t("options.comfortType.standard")}
                    </option>
                    <option value="GOOD">
                      {t("options.comfortType.good")}
                    </option>
                    <option value="HIGH">
                      {t("options.comfortType.high")}
                    </option>
                    <option value="VERY_HIGH_PREMIUM">
                      {t("options.comfortType.veryHighPremium")}
                    </option>
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.streetName")}</RequiredLabel>
                  </label>
                  <input
                    name="street_name"
                    value={propertyDetails.street_name}
                    onChange={handleChange}
                    placeholder={t("placeholders.streetName")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.houseNumber")}</label>
                  <input
                    name="house_number"
                    value={propertyDetails.house_number}
                    onChange={handleChange}
                    placeholder={t("placeholders.houseNumber")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.city")}</RequiredLabel>
                  </label>

                  <input
                    name="city"
                    value={propertyDetails.city}
                    onChange={handleChange}
                    placeholder={t("placeholders.city", "e.g. Antwerp")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.postalCode")}</RequiredLabel>
                  </label>
                  <input
                    name="postal_code"
                    value={propertyDetails.postal_code}
                    onChange={handleChange}
                    placeholder={t("placeholders.postalCode")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.country")}</RequiredLabel>
                  </label>

                  <select
                    name="country"
                    value={propertyDetails.country}
                    onChange={handleChange}
                    disabled={Boolean(dossierId && shouldPrefill)}
                  >
                    <option value="">{t("options.select")}</option>

                    {EUROPEAN_COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>
                      {t("fields.marketRegion", "Market region")}
                    </RequiredLabel>
                  </label>

                  <select
                    name="market_region_id"
                    value={propertyDetails.market_region_id || ""}
                    onChange={handleChange}
                    disabled={
                      Boolean(dossierId && shouldPrefill) ||
                      !propertyDetails.country ||
                      loadingMarketRegions
                    }
                  >
                    <option value="">
                      {!propertyDetails.country
                        ? t(
                            "placeholders.selectCountryFirst",
                            "Select a country first",
                          )
                        : loadingMarketRegions
                          ? t(
                              "placeholders.loadingMarketRegions",
                              "Loading market regions...",
                            )
                          : t(
                              "placeholders.marketRegion",
                              "Select market region",
                            )}
                    </option>
                    {propertyDetails.market_region &&
                    !marketRegions.some(
                      (region) =>
                        String(region.id) ===
                        String(propertyDetails.market_region_id),
                    ) ? (
                      <option value={propertyDetails.market_region_id}>
                        {propertyDetails.market_region.name}
                      </option>
                    ) : null}

                    {marketRegions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </select>

                  {propertyDetails.market_region?.formatted_address ? (
                    <span className="hb-oss-field-hint">
                      {propertyDetails.market_region.formatted_address}
                    </span>
                  ) : null}
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.apartmentNumber")}</label>
                  <input
                    name="apartment_number"
                    value={propertyDetails.apartment_number}
                    onChange={handleChange}
                    placeholder={t("placeholders.apartmentNumber")}
                  />
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>
                      {t("fields.epcRatingCurrent")}
                    </RequiredLabel>
                  </label>
                  <select
                    name="epc_rating_current"
                    value={propertyDetails.epc_rating_current}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    {["A_PLUS", "A", "B", "C", "D", "E", "F", "G", "H"].map(
                      (rating) => (
                        <option key={rating} value={rating}>
                          {rating === "A_PLUS" ? "A+" : rating}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.epcRatingTarget")}</label>
                  <select
                    name="epc_rating_target"
                    value={propertyDetails.epc_rating_target}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    {["A_PLUS", "A", "B", "C", "D", "E", "F", "G", "H"].map(
                      (rating) => (
                        <option key={rating} value={rating}>
                          {rating === "A_PLUS" ? "A+" : rating}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.kwhCurrent")}</RequiredLabel>
                  </label>
                  {renderInputWithSuffix(
                    "kwh_sqm_year_current",
                    propertyDetails.kwh_sqm_year_current,
                    t("placeholders.kwhCurrent"),
                    t("units.kwhPerSqm"),
                  )}
                </div>

                <div className="hb-oss-field">
                  <label>{t("fields.kwhTarget")}</label>
                  {renderInputWithSuffix(
                    "kwh_sqm_year_target",
                    propertyDetails.kwh_sqm_year_target,
                    t("placeholders.kwhTarget"),
                    t("units.kwhPerSqm"),
                  )}
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.heatingType")}</RequiredLabel>
                  </label>
                  <select
                    name="heating_type"
                    value={propertyDetails.heating_type}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="NONE">
                      {t("options.heatingType.none")}
                    </option>
                    <option value="GAS_BOILER">
                      {t("options.heatingType.gasBoiler")}
                    </option>
                    <option value="AIR_CONDITION">
                      {t("options.heatingType.airCondition")}
                    </option>
                    <option value="HEAT_PUMP">
                      {t("options.heatingType.heatPump")}
                    </option>
                    <option value="DISTRICT_HEATING">
                      {t("options.heatingType.districtHeating")}
                    </option>
                  </select>
                </div>

                <div className="hb-oss-field">
                  <label>
                    <RequiredLabel>{t("fields.coolingType")}</RequiredLabel>
                  </label>
                  <select
                    name="cooling_type"
                    value={propertyDetails.cooling_type}
                    onChange={handleChange}
                  >
                    <option value="">{t("options.select")}</option>
                    <option value="NONE">
                      {t("options.coolingType.none")}
                    </option>
                    <option value="AIR_CONDITION">
                      {t("options.coolingType.airCondition")}
                    </option>
                  </select>
                </div>

                <div className="hb-oss-field hb-oss-field--full">
                  <label>{t("fields.uploadEpc")}</label>
                  <label className="hb-oss-upload">
                    <input
                      type="file"
                      hidden
                      accept={EPC_ACCEPT}
                      onChange={handleEpcUpload}
                    />
                    <div className="hb-oss-upload__inner">
                      <Upload size={18} />
                      <span>
                        {epcFileName || t("fields.uploadEpcPlaceholder")}
                      </span>
                    </div>
                  </label>

                  <div className="hb-oss-upload__hint">
                    <FileText size={14} />
                    <span>{t("fields.uploadEpcHint")}</span>
                  </div>
                </div>

                <div className="hb-oss-field hb-oss-field--full">
                  <label>{t("fields.notes")}</label>
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setToastErrors([]);
                    }}
                    placeholder={t("fields.notesPlaceholder")}
                  />
                </div>
              </div>
            </div>

            {shouldPrefill && (
              <div className="hb-oss-property-right">
                <div className="hb-oss-side-card">
                  <div className="hb-oss-side-card__header">
                    <MapPin size={16} />
                    <span>{t("sections.locationSummary")}</span>
                  </div>

                  <div className="hb-oss-side-card__body">
                    <div className="hb-oss-side-row">
                      <span>{t("fields.street")}</span>
                      <strong>
                        {[
                          propertyDetails.street_name,
                          propertyDetails.house_number,
                        ]
                          .filter(Boolean)
                          .join(" ") || t("common.notAvailable")}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.city")}</span>
                      <strong>
                        {propertyDetails.city || t("common.notAvailable")}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.postalCode")}</span>
                      <strong>
                        {propertyDetails.postal_code ||
                          t("common.notAvailable")}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.apartment")}</span>
                      <strong>
                        {propertyDetails.apartment_number ||
                          t("common.notAvailable")}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="hb-oss-side-card">
                  <div className="hb-oss-side-card__header">
                    <Home size={16} />
                    <span>{t("sections.energySnapshot")}</span>
                  </div>

                  <div className="hb-oss-side-card__body">
                    <div className="hb-oss-side-row">
                      <span>{t("fields.currentConsumption")}</span>
                      <strong>
                        {formatNumber(
                          propertyDetails.kwh_sqm_year_current,
                          ` ${t("units.kwhPerSqm")}`,
                          t,
                        )}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.targetConsumption")}</span>
                      <strong>
                        {formatNumber(
                          propertyDetails.kwh_sqm_year_target,
                          ` ${t("units.kwhPerSqm")}`,
                          t,
                        )}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.currentEpc")}</span>
                      <strong>
                        {propertyDetails.epc_rating_current ||
                          t("common.notAvailable")}
                      </strong>
                    </div>

                    <div className="hb-oss-side-row">
                      <span>{t("fields.targetEpc")}</span>
                      <strong>
                        {propertyDetails.epc_rating_target ||
                          t("common.notAvailable")}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hb-oss-property-card">
          <div className="hb-oss-property-card__header">
            <div>
              <h2>
                {t("sections.measures")}
                <span className="hb-oss-required">*</span>
              </h2>
              <p>{t("sections.measuresHint")}</p>
            </div>
          </div>

          <div className="hb-oss-measures-grid">
            {MEASURE_GROUPS.map((group) => (
              <div key={group.key} className="hb-oss-measures-card">
                <div className="hb-oss-measures-card__header">
                  <Wrench size={16} />
                  <span>{t(`measureGroups.${group.key}.title`)}</span>
                </div>

                <div className="hb-oss-measures-list">
                  {group.items.map((item) => {
                    const checked = selectedMeasures.includes(item);

                    return (
                      <button
                        key={item}
                        type="button"
                        className={`hb-oss-measure ${
                          checked ? "is-selected" : ""
                        }`}
                        onClick={() => toggleMeasure(item)}
                      >
                        <span
                          className={`hb-oss-measure__box ${
                            checked ? "is-checked" : ""
                          }`}
                        >
                          {checked ? <CheckCircle2 size={15} /> : null}
                        </span>

                        <span className="hb-oss-measure__content">
                          <span className="hb-oss-measure__text">
                            {t(`measures.${item}.label`)}
                          </span>

                          <span
                            className="hb-oss-measure__info"
                            tabIndex={0}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info size={14} />
                            <span className="hb-oss-measure__tooltip">
                              {t(`measures.${item}.tooltip`)}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hb-oss-property-footer">
          <button
            type="button"
            className="hb-oss-next-btn"
            onClick={handleNext}
            disabled={submitting}
          >
            <span>{submitting ? t("buttons.loading") : t("buttons.next")}</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
